import { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Search, ShieldCheck, BookOpen, MapPin, Shield, Briefcase,
  MessageCircleQuestion, AlertTriangle,
} from "lucide-react";
import { RIGHTS, GLOSSARY, STATES, LAWYER_TIPS } from "@/data/legalData";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const lawyerIcons = { Shield, Briefcase, MessageCircleQuestion, AlertTriangle };

export default function Resources() {
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || "rights");
  const [gq, setGq] = useState("");
  const [selState, setSelState] = useState(location.state?.state || STATES[0].name);

  const filteredGlossary = useMemo(() => {
    const q = gq.toLowerCase().trim();
    if (!q) return GLOSSARY;
    return GLOSSARY.filter((g) => g.term.toLowerCase().includes(q) || g.def.toLowerCase().includes(q));
  }, [gq]);

  const stateData = STATES.find((s) => s.name === selState) || STATES[0];

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Resource library</span>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">Everything you need, in plain English</h1>
          <p className="mt-4 text-ink-700 leading-relaxed">Your rights, a plain-language glossary, a 50-state + federal procedure explorer, and how to choose the right lawyer.</p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="bg-parchment-100 border border-parchment-200 rounded-full p-1 h-auto flex-wrap">
            <TabsTrigger data-testid="resources-tab-rights" value="rights" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><ShieldCheck className="h-4 w-4" /> Your Rights</TabsTrigger>
            <TabsTrigger data-testid="resources-tab-glossary" value="glossary" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><BookOpen className="h-4 w-4" /> Glossary</TabsTrigger>
            <TabsTrigger data-testid="resources-tab-states" value="states" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><MapPin className="h-4 w-4" /> 50-State Explorer</TabsTrigger>
            <TabsTrigger data-testid="resources-tab-lawyer" value="lawyer" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><Briefcase className="h-4 w-4" /> Finding a Lawyer</TabsTrigger>
          </TabsList>

          {/* RIGHTS */}
          <TabsContent value="rights" className="mt-8">
            <div className="grid sm:grid-cols-2 gap-5">
              {RIGHTS.map((r, i) => (
                <div key={r.title} data-testid={`right-card-${i}`} className="rounded-2xl border border-parchment-200 bg-white p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="grid place-items-center h-10 w-10 rounded-xl bg-forest-100 text-forest-800"><ShieldCheck className="h-5 w-5" /></span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-forest-600 bg-forest-100 px-2 py-1 rounded-full">{r.statute}</span>
                  </div>
                  <h3 className="mt-2 font-semibold text-forest-900 text-lg">{r.title}</h3>
                  <p className="mt-1.5 text-sm text-ink-700 leading-relaxed">{r.body}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* GLOSSARY */}
          <TabsContent value="glossary" className="mt-8">
            <div className="relative max-w-md mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
              <Input data-testid="glossary-search" value={gq} onChange={(e) => setGq(e.target.value)}
                placeholder="Search 30+ legal terms…" className="pl-11 rounded-full border-parchment-200 bg-white h-11" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredGlossary.map((g) => (
                <div key={g.term} data-testid={`glossary-term-${g.term}`} className="rounded-2xl border border-parchment-200 bg-white p-5">
                  <h3 className="font-serif text-xl font-semibold text-forest-900">{g.term}</h3>
                  <p className="mt-1 text-sm text-ink-700 leading-relaxed">{g.def}</p>
                </div>
              ))}
              {filteredGlossary.length === 0 && (
                <p className="text-ink-500 col-span-full">No terms match "{gq}".</p>
              )}
            </div>
          </TabsContent>

          {/* STATES */}
          <TabsContent value="states" className="mt-8">
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-4">
                <div className="rounded-2xl border border-parchment-200 bg-white p-5 sticky top-24">
                  <p className="font-mono text-xs uppercase tracking-wider text-forest-600 mb-3">Select jurisdiction</p>
                  <Select value={selState} onValueChange={setSelState}>
                    <SelectTrigger data-testid="states-select" className="rounded-xl border-parchment-200 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      {STATES.map((s) => <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-4 text-xs text-ink-400 leading-relaxed">Procedural timelines are general summaries and vary by county and court. Always confirm with a local attorney.</p>
                </div>
              </div>
              <div className="lg:col-span-8">
                <div data-testid="state-detail" className="rounded-3xl border border-parchment-200 bg-white p-8">
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">[{stateData.abbr}]</span>
                  <h2 className="mt-2 font-serif text-3xl font-semibold text-forest-900">{stateData.name}</h2>
                  <div className="mt-6 space-y-5">
                    <Row label="Court system" value={stateData.court} />
                    <Row label="Speedy trial" value={stateData.speedy} />
                    <Row label="Bail & release" value={stateData.bail} />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* LAWYER */}
          <TabsContent value="lawyer" className="mt-8">
            <div className="grid sm:grid-cols-2 gap-5">
              {LAWYER_TIPS.map((t) => {
                const Icon = lawyerIcons[t.icon] || Briefcase;
                return (
                  <div key={t.title} data-testid={`lawyer-tip-${t.title}`} className="rounded-2xl border border-parchment-200 bg-white p-6">
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-forest-100 text-forest-800"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 font-semibold text-forest-900 text-lg">{t.title}</h3>
                    <p className="mt-1.5 text-sm text-ink-700 leading-relaxed">{t.body}</p>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 border-b border-parchment-200 pb-4 last:border-0">
    <span className="font-mono text-xs uppercase tracking-wider text-forest-600 sm:w-40 shrink-0 pt-0.5">{label}</span>
    <span className="text-ink-700 leading-relaxed">{value}</span>
  </div>
);
